import { useLayoutEffect, useMemo, useRef, useState } from "react";

export const SHELL_TABLE_BORDER = "1px solid rgba(210, 218, 230, 1)";

export const SHELL_TABLE_HEADER_BACKGROUND = "hsla(250, 86%, 97%, 1)";

export const SHELL_TABLE_HEADER_HEIGHT = 58;
export const SHELL_TABLE_ROW_HEIGHT = 40;

const EMPTY_COLUMNS = [];

const getColumnId = (column) => String(column?.id || column?.accessorKey || "");

const applyResponsiveColumnWidth = (column, widthPx) => {
  const safeWidth = Math.max(1, widthPx);

  const currentHeadProps =
    column?.muiTableHeadCellProps &&
    typeof column.muiTableHeadCellProps === "object"
      ? column.muiTableHeadCellProps
      : {};

  const currentBodyProps =
    column?.muiTableBodyCellProps &&
    typeof column.muiTableBodyCellProps === "object"
      ? column.muiTableBodyCellProps
      : {};

  return {
    ...column,

    size: safeWidth,
    minSize: safeWidth,
    maxSize: safeWidth,
    grow: false,

    muiTableHeadCellProps: {
      ...currentHeadProps,

      sx: {
        ...(currentHeadProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",
      },
    },

    muiTableBodyCellProps: {
      ...currentBodyProps,

      sx: {
        ...(currentBodyProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",
      },
    },
  };
};

export const useResponsiveShellTable = (
  rawColumns = [],
  columnWeights = {},
  extraWeight = 0,
) => {
  const viewportRef = useRef(null);

  const [tableWidth, setTableWidth] = useState(1000);

  useLayoutEffect(() => {
    let frameId = null;

    const updateWidth = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const element = viewportRef.current;

        if (!element) return;

        const rect = element.getBoundingClientRect();

        const viewportWidth = document.documentElement.clientWidth;

        const visibleLeft = Math.max(rect.left, 0);

        const visibleRight = Math.min(rect.right, viewportWidth);

        const nextWidth = Math.max(
          1,
          Math.floor(visibleRight - visibleLeft - 1),
        );

        setTableWidth((previous) =>
          previous === nextWidth ? previous : nextWidth,
        );
      });
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;

    const element = viewportRef.current;

    if (observer && element) {
      observer.observe(element);

      if (element.parentElement) {
        observer.observe(element.parentElement);
      }
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", updateWidth);

      observer?.disconnect();
    };
  }, []);

  const headerFontSize = useMemo(
    () => Math.max(8, Math.min(14, tableWidth / 75)),
    [tableWidth],
  );

  const bodyFontSize = useMemo(
    () => Math.max(8, Math.min(14, tableWidth / 75)),
    [tableWidth],
  );

  const safeColumns = Array.isArray(rawColumns) ? rawColumns : EMPTY_COLUMNS;

  const { columns, extraWidth } = useMemo(() => {
    const columnsWeight = safeColumns.reduce((total, column) => {
      const id = getColumnId(column);

      return total + (columnWeights[id] ?? 1);
    }, 0);

    const totalWeight = columnsWeight + extraWeight;

    const usableWidth = Math.max(1, tableWidth - 2);

    const responsiveColumns = safeColumns.map((column) => {
      const id = getColumnId(column);

      const weight = columnWeights[id] ?? 1;

      const width = (weight / totalWeight) * usableWidth;

      return applyResponsiveColumnWidth(column, width);
    });

    return {
      columns: responsiveColumns,

      extraWidth:
        extraWeight > 0
          ? Math.max(1, (extraWeight / totalWeight) * usableWidth)
          : 0,
    };
  }, [safeColumns, columnWeights, extraWeight, tableWidth]);

  return {
    viewportRef,
    tableWidth,
    headerFontSize,
    bodyFontSize,
    columns,
    extraWidth,
  };
};

export const getResponsiveShellTableConfig = ({
  tableWidth,
  headerFontSize,
  bodyFontSize,
}) => {
  const headCellSx = {
    boxSizing: "border-box",

    height: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,
    minHeight: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,
    maxHeight: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,

    backgroundColor: `${SHELL_TABLE_HEADER_BACKGROUND} !important`,

    borderRight: `${SHELL_TABLE_BORDER} !important`,
    borderBottom: `${SHELL_TABLE_BORDER} !important`,

    p: "0 !important",
    px: "2px !important",
    py: "0 !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    textAlign: "center",
    verticalAlign: "middle",

    overflow: "hidden",

    fontSize: `${headerFontSize}px !important`,
    fontWeight: "700 !important",
    lineHeight: "1.1 !important",
    "& .Mui-TableHeadCell-Content": {
      width: "100%",
      minWidth: "0 !important",
      maxWidth: "100%",
      height: "100%",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      overflow: "hidden",
    },

    "& .Mui-TableHeadCell-Content-Labels": {
      width: "100%",
      minWidth: "0 !important",
      maxWidth: "100%",
      height: "100%",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      overflow: "hidden",
    },

    "& .Mui-TableHeadCell-Content-Wrapper": {
      width: "100%",
      minWidth: "0 !important",
      maxWidth: "100%",
      height: "100%",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      textAlign: "center",

      whiteSpace: "normal !important",

      overflow: "hidden !important",

      fontSize: `${headerFontSize}px !important`,
      fontWeight: "700 !important",
      lineHeight: "1.1 !important",
    },

    "& .Mui-TableHeadCell-Content-Wrapper > *": {
      width: "100% !important",
      minWidth: "0 !important",
      maxWidth: "100% !important",

      margin: "0 !important",

      textAlign: "center !important",
    },
  };

  const bodyCellSx = {
    boxSizing: "border-box",

    height: `${SHELL_TABLE_ROW_HEIGHT}px !important`,
    minHeight: `${SHELL_TABLE_ROW_HEIGHT}px !important`,
    maxHeight: `${SHELL_TABLE_ROW_HEIGHT}px !important`,

    display: "flex",
    alignItems: "center",

    p: "0 !important",

    minWidth: "0 !important",

    overflow: "hidden",

    borderRight: `${SHELL_TABLE_BORDER} !important`,
    borderBottom: `${SHELL_TABLE_BORDER} !important`,

    fontSize: `${bodyFontSize}px !important`,
    fontWeight: "500 !important",

    "& .Mui-TableBodyCell-Content-Wrapper": {
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      height: "100%",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      overflow: "hidden",
      textOverflow: "ellipsis",

      fontSize: `${bodyFontSize}px !important`,
    },
  };

  const editFieldSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",

    height: `${SHELL_TABLE_ROW_HEIGHT}px`,

    overflow: "hidden",

    "& .MuiInputBase-root": {
      width: "100%",
      minWidth: "0 !important",
      maxWidth: "100%",

      height: `${SHELL_TABLE_ROW_HEIGHT}px !important`,

      overflow: "hidden",
    },

    "& .MuiInputBase-input": {
      width: "100%",
      minWidth: "0 !important",

      boxSizing: "border-box",

      height: `${SHELL_TABLE_ROW_HEIGHT}px`,

      padding: "0 2px",

      textAlign: "center",

      fontSize: `${bodyFontSize}px !important`,
    },

    "& .MuiSelect-select": {
      height: `${SHELL_TABLE_ROW_HEIGHT}px !important`,

      boxSizing: "border-box",

      display: "flex",
      alignItems: "center",

      py: "0 !important",

      textAlign: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const mrtProps = {
    layoutMode: "grid-no-grow",

    defaultColumn: {
      size: 1,
      minSize: 1,
      maxSize: 100000,
      grow: false,
    },

    enableTopToolbar: false,

    muiTableProps: {
      sx: {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        overflow: "hidden !important",
      },
    },

    muiTablePaperProps: {
      elevation: 0,

      sx: {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        overflowX: "hidden !important",
      },
    },

    muiTableContainerProps: {
      sx: {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        overflowX: "hidden !important",

        "& .MuiTable-root": {
          width: `${tableWidth}px !important`,
          minWidth: `${tableWidth}px !important`,
          maxWidth: `${tableWidth}px !important`,
        },

        "& .MuiTableHead-root, & .MuiTableBody-root": {
          width: `${tableWidth}px !important`,
          minWidth: `${tableWidth}px !important`,
          maxWidth: `${tableWidth}px !important`,

          overflow: "hidden",
        },

        "& .MuiTableRow-root": {
          width: `${tableWidth}px !important`,
          minWidth: `${tableWidth}px !important`,
          maxWidth: `${tableWidth}px !important`,

          overflow: "hidden",
        },

        "& .MuiTableCell-root": {
          minWidth: "0 !important",

          boxSizing: "border-box",

          overflow: "hidden",
        },

        "& .MuiTextField-root, & .MuiFormControl-root": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",
        },

        "& .MuiInputBase-root": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          overflow: "hidden",
        },

        "& .MuiInputBase-input": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          boxSizing: "border-box",
        },

        "& .MuiSelect-select": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          overflow: "hidden !important",
          textOverflow: "ellipsis",
        },
      },
    },

    muiTableHeadRowProps: {
      sx: {
        display: "flex !important",

        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        height: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,
        minHeight: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,
        maxHeight: `${SHELL_TABLE_HEADER_HEIGHT}px !important`,

        alignItems: "stretch",

        overflow: "hidden",

        backgroundColor: SHELL_TABLE_HEADER_BACKGROUND,
      },
    },

    muiTableBodyRowProps: {
      sx: {
        display: "flex !important",

        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        height: `${SHELL_TABLE_ROW_HEIGHT}px !important`,
        minHeight: `${SHELL_TABLE_ROW_HEIGHT}px !important`,
        maxHeight: `${SHELL_TABLE_ROW_HEIGHT}px !important`,

        alignItems: "stretch",

        overflow: "hidden",
      },
    },
  };

  return {
    mrtProps,
    headCellSx,
    bodyCellSx,
    editFieldSx,
  };
};
